/**
 * Custom currency converter that only uses rates from baCurr — verbatim from
 * v1 nova_currency.js (research/06 §7.1–7.3; REQ-CUR-2). No network I/O;
 * conversion is `amount * rates[from]/rates[to]`, unrounded;
 * `getConvertedAmount` THROWS on a missing rate and cart callers do not
 * catch. Console output incl. the `"Fromatted amount:"` typo preserved
 * (REQ-CUR-6). The formats table lives in ./formats.ts (SPEC §2.1).
 */
import { formats } from "./formats";

export const CurrencyConverter = (rates: any): any => {
    function formatNumber(value: any, decimalPlaces?: any, thousandsSeparator?: any, decimalSeparator?: any): any {
        decimalPlaces = decimalPlaces === undefined ? 2 : decimalPlaces;
        thousandsSeparator = thousandsSeparator === undefined ? "," : thousandsSeparator;
        decimalSeparator = decimalSeparator === undefined ? "." : decimalSeparator;
        if (isNaN(value) || value == null) return 0;
        let fixedValue = Number(value).toFixed(decimalPlaces);
        let parts = fixedValue.split(".");
        let integerPart = parts[0]!.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousandsSeparator);
        return integerPart + (parts[1] ? decimalSeparator + parts[1] : "");
    }

    // Retrieve the appropriate format for a currency
    function getFormat(currency: any, withCurrencyCode = false): string {
        const formatSettings = formats[currency] || {
            money_format: "{{amount}}",
            money_with_currency_format: "{{amount}} " + currency
        };
        return withCurrencyCode ? formatSettings.money_with_currency_format : formatSettings.money_format;
    }

    // Format the amount based on currency format settings
    function formatCurrency(amount: any, currency: any, withCurrencyCode = false): string {
        // Get format template and determine decimal places
        const formatTemplate = getFormat(currency, withCurrencyCode);
        const decimals = ["KWD", "JOD", "OMR", "BHD"].includes(currency) ? 3 : 2;
        let formatType = formatTemplate.split("{{").pop()!.split("}}")[0];
        // Format the amount (adding thousands separators and decimals)
        var formattedAmount: any = "";
        switch (formatType) {
            case "amount":
              formattedAmount = formatNumber(amount, decimals);
              break;
            case "amount_no_decimals":
              formattedAmount = formatNumber(amount, 0);
              break;
            case "amount_no_decimals_with_comma_separator":
              formattedAmount = formatNumber(amount, 0, ".", ",");
              break;
            case "amount_with_comma_separator":
              formattedAmount = formatNumber(amount, decimals, ".", ",");
              break;
            default:
              formattedAmount = formatNumber(amount, decimals, ",", ".");
        }

        // Replace placeholder in format template with the formatted amount
        let finalOutput = formatTemplate.replace("{{" + formatType + "}}", formattedAmount);
        return finalOutput;
    }

    // Convert currency from source to target, and format the result
    function getConvertedAmount(amount: any, fromCurrency: any, toCurrency: any): any {
        // Check that conversion rates for both currencies exist
        if (!rates || !rates[fromCurrency] || !rates[toCurrency]) {
            throw new Error(`Currency rate not found for ${fromCurrency} or ${toCurrency}`);
        }

        // Calculate conversion rate and convert the amount
        const conversionRate = rates[fromCurrency] / rates[toCurrency];
        const converted = amount * conversionRate;
        console.log(`Converted ${amount} from ${fromCurrency} to ${toCurrency}:`, converted);
        return converted;
    }

    // Convert currency from source to target, and format the result
    function convert(amount: any, fromCurrency: any, toCurrency: any, withCurrencyCode = false): string {
        const amountAfterConversion = getConvertedAmount(amount, fromCurrency, toCurrency);
        // Format and return the converted amount
        const formattedValue = formatCurrency(amountAfterConversion, toCurrency, withCurrencyCode);
        console.log(`Fromatted amount:${amountAfterConversion}, ${toCurrency}, ${withCurrencyCode} and ${formattedValue}`);
        return formattedValue;
    }

    // Return an object exposing the methods
    return {
        getFormat,
        formatCurrency,
        convert,
        getConvertedAmount
    };
};
